import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { BaseUrls } from '@app/app.constants';
import { AddPresenceProfile } from '@app/core/interfaces/add-presence-profile';
import { PresenceProfile, PresenceProfileSettingsUpdate } from '@app/core/interfaces/presence-profile';
import { UpdatePresenceProfile } from '@app/core/interfaces/update-presence-profile';
import { UserLevel } from '@app/core/models/user-level';
import { Utils } from '@app/shared/services/utils.service';
import { wrapError } from '@app/utils/rxjs';
import { Observable } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class RichPresenceProfileRepository {
  constructor(
    private http: HttpClient,
    private utils: Utils
  ) {}

  activate(): Observable<PresenceProfile[]> {
    return this.utils
      .apiUrl({
        [UserLevel.ServiceProviderAdmin]: `${BaseUrls.ServiceProvider.EndUser}/presenceProfileMechanism`,
        [UserLevel.EnterpriseAdmin]: `${BaseUrls.Enterprise.EndUser}/presenceProfileMechanism`,
        [UserLevel.EndUser]: `/user/presenceProfileMechanism`,
      })
      .pipe(
        mergeMap((url) =>
          this.http.put<PresenceProfile[]>(url, undefined, {
            params: new HttpParams().append('active', true),
          })
        ),
        wrapError('rich-presence-profiles:activate')
      );
  }

  create(profile: AddPresenceProfile): Observable<PresenceProfile> {
    return this.utils
      .apiUrl({
        [UserLevel.ServiceProviderAdmin]: `${BaseUrls.ServiceProvider.EndUser}/presenceProfiles`,
        [UserLevel.EnterpriseAdmin]: `${BaseUrls.Enterprise.EndUser}/presenceProfiles`,
        [UserLevel.EndUser]: `/user/presenceProfiles`,
      })
      .pipe(
        mergeMap((url) => this.http.post<PresenceProfile>(url, profile)),
        wrapError('rich-presence-profiles:create')
      );
  }

  delete(id: number): Observable<void> {
    return this.utils
      .apiUrl({
        [UserLevel.ServiceProviderAdmin]: `${BaseUrls.ServiceProvider.EndUser}/presenceProfiles/${id}`,
        [UserLevel.EnterpriseAdmin]: `${BaseUrls.Enterprise.EndUser}/presenceProfiles/${id}`,
        [UserLevel.EndUser]: `/user/presenceProfiles/${id}`,
      })
      .pipe(
        mergeMap((url) => this.http.delete<void>(url)),
        wrapError('rich-presence-profiles:delete')
      );
  }

  get(id: number): Observable<PresenceProfile> {
    return this.utils
      .apiUrl({
        [UserLevel.ServiceProviderAdmin]: `${BaseUrls.ServiceProvider.EndUser}/presenceProfiles/${id}`,
        [UserLevel.EnterpriseAdmin]: `${BaseUrls.Enterprise.EndUser}/presenceProfiles/${id}`,
        [UserLevel.EndUser]: `/user/presenceProfiles/${id}`,
      })
      .pipe(
        mergeMap((url) => this.http.get<PresenceProfile>(url)),
        wrapError('rich-presence-profiles:get')
      );
  }

  list(params?: Params): Observable<PresenceProfile[]> {
    return this.utils
      .apiUrl(
        {
          [UserLevel.ServiceProviderAdmin]: `${BaseUrls.ServiceProvider.EndUser}/presenceProfiles`,
          [UserLevel.EnterpriseAdmin]: `${BaseUrls.Enterprise.EndUser}/presenceProfiles`,
          [UserLevel.EndUser]: `/user/presenceProfiles`,
        },
        params
      )
      .pipe(
        mergeMap((url) => this.http.get<PresenceProfile[]>(url)),
        wrapError('rich-presence-profiles:list')
      );
  }

  update(id: number, profile: UpdatePresenceProfile): Observable<PresenceProfile> {
    return this.utils
      .apiUrl({
        [UserLevel.ServiceProviderAdmin]: `${BaseUrls.ServiceProvider.EndUser}/presenceProfiles/${id}`,
        [UserLevel.EnterpriseAdmin]: `${BaseUrls.Enterprise.EndUser}/presenceProfiles/${id}`,
        [UserLevel.EndUser]: `/user/presenceProfiles/${id}`,
      })
      .pipe(
        mergeMap((url) => this.http.put<PresenceProfile>(url, profile)),
        wrapError('rich-presence-profiles:update')
      );
  }

  setAsDefault(id: number, notDefault?: boolean): Observable<void> {
    const deactivateAsDefault = notDefault ?? false;
    return this.utils
      .apiUrl({
        [UserLevel.ServiceProviderAdmin]: `${BaseUrls.ServiceProvider.EndUser}/presenceProfiles/${id}/asDefault`,
        [UserLevel.EnterpriseAdmin]: `${BaseUrls.Enterprise.EndUser}/presenceProfiles/${id}/asDefault`,
        [UserLevel.EndUser]: `/user/presenceProfiles/${id}/asDefault`,
      })
      .pipe(
        mergeMap((url) =>
          this.http.put<void>(url, undefined, {
            params: new HttpParams().append('deactivate', deactivateAsDefault),
          })
        ),
        wrapError('rich-presence-profiles:asDefault')
      );
  }

  /**
   *
   * @param type
   * @param profileId
   * @param data optional data to be sent as the request payload, e.g., such as CC/ACD agent's state in queues
   * @returns
   */
  updateSettings(type: string, profileId: number, data: PresenceProfileSettingsUpdate): Observable<void> {
    return this.utils
      .apiUrl({
        [UserLevel.ServiceProviderAdmin]: `${BaseUrls.ServiceProvider.EndUser}/presenceProfiles/${profileId}/settings`,
        [UserLevel.EnterpriseAdmin]: `${BaseUrls.Enterprise.EndUser}/presenceProfiles/${profileId}/settings`,
        [UserLevel.EndUser]: `/user/presenceProfiles/${profileId}/settings`,
      })
      .pipe(
        mergeMap((url) =>
          this.http.put<void>(url, data, {
            params: new HttpParams().append('context', type),
          })
        ),
        wrapError('rich-presence-profiles:update-settings')
      );
  }
}
